import { useState, useEffect, useRef, useMemo } from 'react';
import '../css/service-modal.css';

export default function ServiceModal({ service, onClose }) {
  const media = useMemo(() => {
    const images = service.images?.length ? service.images
                 : service.imageUrl ? [service.imageUrl] : [];
    const videos = service.videos || [];
    return [
      ...videos.map(url => ({ type: 'video', url })),
      ...images.map(url => ({ type: 'image', url })),
    ];
  }, [service]);

  const [idx, setIdx] = useState(0);
  const [lightbox, setLightbox] = useState(null); // url of image to show fullscreen
  const videoRef = useRef(null);

  const current = media[idx];
  const isVideo = current?.type === 'video';

  // Play/pause video when slide changes
  useEffect(() => {
    if (!videoRef.current) return;
    if (isVideo) {
      videoRef.current.play().catch(() => {});
    } else {
      videoRef.current.pause();
    }
  }, [idx, isVideo]);

  // Close on Escape
  useEffect(() => {
    const onKey = e => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const prev = e => { e.stopPropagation(); setIdx(i => (i - 1 + media.length) % media.length); };
  const next = e => { e.stopPropagation(); setIdx(i => (i + 1) % media.length); };

  return (
    <div className="sm-overlay" onClick={onClose}>
      <div className={`sm-modal ${isVideo ? 'sm-modal-video' : ''}`} onClick={e => e.stopPropagation()}>
        <button className="sm-close" onClick={onClose}>✕</button>

        {media.length > 0 ? (
          <div className={`sm-carousel ${isVideo ? 'sm-carousel-video' : ''}`}>

            {/* Show current item only — video or image */}
            {isVideo ? (
              <video
                ref={videoRef}
                key={current.url}
                src={current.url}
                className="sm-video-player"
                controls
                controlsList="nodownload noremoteplayback"
                playsInline
                loop
                onContextMenu={e => e.preventDefault()}
              />
            ) : (
              <img
                key={current.url}
                src={current.url}
                alt={`${service.title} ${idx + 1}`}
                className="sm-slide sm-slide-clickable"
                onClick={e => { e.stopPropagation(); setLightbox(current.url); }}
                onContextMenu={e => e.preventDefault()}
                title="Click to view full size"
              />
            )}

            {/* Arrows */}
            {media.length > 1 && (
              <>
                <button className="sm-arr sm-arr-l" onClick={prev}>‹</button>
                <button className="sm-arr sm-arr-r" onClick={next}>›</button>
                <div className="sm-dots">
                  {media.map((m, i) => (
                    <button
                      key={i}
                      className={`sm-dot ${i === idx ? 'active' : ''} ${m.type === 'video' ? 'sm-dot-video' : ''}`}
                      onClick={e => { e.stopPropagation(); setIdx(i); }}
                      title={m.type === 'video' ? 'Video' : `Photo ${i + 1}`}
                    />
                  ))}
                </div>
                <span className="sm-media-counter">{idx + 1} / {media.length}</span>
              </>
            )}

            {isVideo && <span className="sm-media-badge">▶ Video</span>}
          </div>
        ) : (
          <div className="sm-no-img"><span>{service.icon}</span></div>
        )}

        {/* Fullscreen image lightbox */}
        {lightbox && (
          <div className="sm-lightbox" onClick={() => setLightbox(null)}>
            <button className="sm-lightbox-close" onClick={() => setLightbox(null)}>✕</button>
            <img
              src={lightbox}
              alt="Full size"
              className="sm-lightbox-img"
              onClick={e => e.stopPropagation()}
              onContextMenu={e => e.preventDefault()}
            />
          </div>
        )}

        <div className="sm-body">
          <div className="sm-title-row">
            <span className="sm-icon">{service.icon}</span>
            <h2 className="sm-title">{service.title}</h2>
          </div>
          <ul className="sm-list">
            {(service.items || []).map((item, i) => (
              <li key={i}><span className="sm-bullet">—</span>{item}</li>
            ))}
          </ul>
          <button
            className="sm-book btn-primary"
            onClick={() => { onClose(); document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' }); }}
          >
            Book This Service →
          </button>
        </div>
      </div>
    </div>
  );
}
